/**
 * Off-main-thread twin of the `d3-regression` loess/log fits used by AdvancedScatter & ManeuverScatter.
 *
 * Those components call `regressionLoess().bandwidth(0.5)` / `regressionLog()` SYNCHRONOUSLY on the main
 * thread inside their fit draw, recomputing every group's curve on each selection/zoom change. `d3-regression`
 * cannot be imported inside a @nodets task (tasks ship by source, no lib imports), so this module contains a
 * faithful, self-contained transcription of d3-regression's loess.js + logarithmic.js and their vega-statistics
 * utils (points/ols/median/interpose/geometry), specialised to the two configs the app uses (loess bandwidth
 * 0.5, log base e). `__tests__/unit/scatterRegressionTask.test.ts` tolerance-checks this against the real
 * `d3-regression` so the curves stay identical.
 *
 * Fan-out is per fit-group: the component prepares each group's sorted {x,y} points (cheap) on the main
 * thread, then `computeScatterFitValues` maps the expensive regressions across the pool.
 */
/* eslint-disable @typescript-eslint/no-explicit-any --
   The d3-regression / vega-statistics accessors are generic (d => d[0]); this faithful transcription keeps
   the same `any` accessor signatures rather than diverging from the source it must match byte-for-byte. */
import { createPool, type Pool } from '../../utils/parallel/poolManager';

export interface ScatterFitPayload {
  /** Points already filtered to finite x/y and sorted ascending by x (as the components do). */
  xyValues: Array<{ x: number; y: number }>;
  /** true → loess(bandwidth); false → logarithmic (base e). */
  useLoess: boolean;
  /** loess bandwidth (ignored for log); the app uses 0.5. */
  bandwidth: number;
}

/**
 * Self-contained pool task — MUST NOT reference anything outside its own body. Verbatim transcription of
 * d3-regression (loess.js, logarithmic.js) + utils, returning the smoothed points as {x,y}[] (the shape the
 * callers build via `.map(([x,y]) => ({x,y}))`).
 */
export function scatterRegressionTask(payload: ScatterFitPayload): Array<{ x: number; y: number }> {
  const { xyValues, useLoess, bandwidth } = payload;
  const getX = (d: { x: number; y: number }) => d.x;
  const getY = (d: { x: number; y: number }) => d.y;

  // --- vega-statistics ols ---
  function ols(uX: number, uY: number, uXY: number, uX2: number): [number, number] {
    const delta = uX2 - uX * uX;
    const slope = Math.abs(delta) < 1e-24 ? 0 : (uXY - uX * uY) / delta;
    const intercept = uY - slope * uX;
    return [intercept, slope];
  }

  // --- vega-statistics points (sorted, mean-centred) ---
  function points(
    data: Array<{ x: number; y: number }>,
    x: (d: any, i?: number) => number,
    y: (d: any, i?: number) => number,
    sort: boolean
  ): [Float64Array, Float64Array, number, number] {
    data = data.filter((d, i) => {
      const u = x(d, i);
      const v = y(d, i);
      return u != null && isFinite(u) && v != null && isFinite(v);
    });
    if (sort) {
      data.sort((a, b) => x(a) - x(b));
    }
    const n = data.length;
    const X = new Float64Array(n);
    const Y = new Float64Array(n);
    let ux = 0;
    let uy = 0;
    let xv: number;
    let yv: number;
    let d: any;
    for (let i = 0; i < n; ) {
      d = data[i];
      X[i] = xv = +x(d, i);
      Y[i] = yv = +y(d, i);
      ++i;
      ux += (xv - ux) / i;
      uy += (yv - uy) / i;
    }
    for (let i = 0; i < n; ++i) {
      X[i] -= ux;
      Y[i] -= uy;
    }
    return [X, Y, ux, uy];
  }

  function visitPoints(
    data: Array<{ x: number; y: number }>,
    x: (d: any, i?: number) => number,
    y: (d: any, i?: number) => number,
    cb: (dx: number, dy: number, i: number) => void
  ): void {
    let iterations = 0;
    for (let i = 0, n = data.length; i < n; i++) {
      const d = data[i];
      const dx = +x(d, i);
      const dy = +y(d, i);
      if (dx != null && isFinite(dx) && dy != null && isFinite(dy)) {
        cb(dx, dy, iterations++);
      }
    }
  }

  // --- median (mutates, as in d3-regression) ---
  function median(arr: Float64Array | number[]): number {
    (arr as number[]).sort((a: number, b: number) => a - b);
    const i = arr.length / 2;
    return i % 1 === 0 ? (arr[i - 1] + arr[i]) / 2 : arr[Math.floor(i)];
  }

  // --- geometry + interpose (logarithmic output) ---
  function angle(line: number[][]): number {
    return (Math.atan2(line[1][1] - line[0][1], line[1][0] - line[0][0]) * 180) / Math.PI;
  }
  function midpoint(line: number[][]): number[] {
    return [(line[0][0] + line[1][0]) / 2, (line[0][1] + line[1][1]) / 2];
  }
  function interpose(xmin: number, xmax: number, predict: (x: number) => number): number[][] {
    const l = ((Math.log(xmax - xmin) * Math.LOG10E + 1) | 0);
    const precision = 1 * Math.pow(10, -l / 2 - 1);
    const maxIter = 1e4;
    const px = (x: number): number[] => [x, predict(x)];
    const pts: number[][] = [px(xmin), px(xmax)];
    let iter = 0;
    const find = (): boolean => {
      iter++;
      const n = pts.length;
      let found = false;
      for (let i = 0; i < n - 1; i++) {
        const p0 = pts[i];
        const p1 = pts[i + 1];
        const m = midpoint([p0, p1]);
        const mp = px(m[0]);
        const a0 = angle([p0, m]);
        const a1 = angle([p0, mp]);
        const a = Math.abs(a0 - a1);
        if (a > precision) {
          pts.splice(i + 1, 0, mp);
          found = true;
        }
      }
      return found;
    };
    while (find() && iter < maxIter) { /* subdivide until the interpolated line is smooth */ }
    return pts;
  }

  // --- loess (Cleveland; d3-regression loess.js) ---
  function tricube(x: number): number {
    return (x = 1 - x * x * x) * x * x;
  }
  function updateInterval(xv: Float64Array, i: number, interval: number[]): void {
    const val = xv[i];
    let left = interval[0];
    let right = interval[1] + 1;
    if (right >= xv.length) {return;}
    while (i > left && xv[right] - val <= val - xv[left]) {
      interval[0] = ++left;
      interval[1] = right;
      ++right;
    }
  }
  function loessOutput(xv: Float64Array, yhat: Float64Array, ux: number, uy: number): number[][] {
    const n = xv.length;
    const out: number[][] = [];
    let i = 0;
    let cnt = 0;
    let prev: number[] = [];
    let v: number;
    for (; i < n; ++i) {
      v = xv[i] + ux;
      if (prev[0] === v) {
        prev[1] += (yhat[i] - prev[1]) / ++cnt;
      } else {
        cnt = 0;
        prev[1] += uy;
        prev = [v, yhat[i]];
        out.push(prev);
      }
    }
    prev[1] += uy;
    return out;
  }
  function loessFit(data: Array<{ x: number; y: number }>, bw0: number): number[][] {
    const maxiters = 2;
    const epsilon = 1e-12;
    const [xv, yv, ux, uy] = points(data, getX, getY, true);
    const n = xv.length;
    const bw = Math.max(2, ~~(bw0 * n));
    const yhat = new Float64Array(n);
    const residuals = new Float64Array(n);
    const robustWeights = new Float64Array(n).fill(1);

    for (let iter = -1; ++iter <= maxiters; ) {
      const interval = [0, bw - 1];
      for (let i = 0; i < n; ++i) {
        const dx = xv[i];
        const i0 = interval[0];
        const i1 = interval[1];
        const edge = dx - xv[i0] > xv[i1] - dx ? i0 : i1;
        let W = 0;
        let X = 0;
        let Y = 0;
        let XY = 0;
        let X2 = 0;
        const denom = 1 / Math.abs(xv[edge] - dx || 1);
        for (let k = i0; k <= i1; ++k) {
          const xk = xv[k];
          const yk = yv[k];
          const w = tricube(Math.abs(dx - xk) * denom) * robustWeights[k];
          const xkw = xk * w;
          W += w;
          X += xkw;
          Y += yk * w;
          XY += yk * xkw;
          X2 += xk * xkw;
        }
        const [a, b] = ols(X / W, Y / W, XY / W, X2 / W);
        yhat[i] = a + b * dx;
        residuals[i] = Math.abs(yv[i] - yhat[i]);
        updateInterval(xv, i + 1, interval);
      }
      if (iter === maxiters) {break;}
      const medianResidual = median(residuals);
      if (Math.abs(medianResidual) < epsilon) {break;}
      for (let i = 0, arg, w; i < n; ++i) {
        arg = residuals[i] / (6 * medianResidual);
        robustWeights[i] = arg >= 1 ? epsilon : (w = 1 - arg * arg) * w;
      }
    }
    return loessOutput(xv, yhat, ux, uy);
  }

  // --- logarithmic (base e; d3-regression logarithmic.js) ---
  function logFit(data: Array<{ x: number; y: number }>): number[][] {
    let n = 0;
    let X = 0;
    let Y = 0;
    let XY = 0;
    let X2 = 0;
    let xmin = Infinity;
    let xmax = -Infinity;
    const lb = Math.log(Math.E);
    visitPoints(data, getX, getY, (dx, dy) => {
      const lx = Math.log(dx) / lb;
      ++n;
      X += (lx - X) / n;
      Y += (dy - Y) / n;
      XY += (lx * dy - XY) / n;
      X2 += (lx * lx - X2) / n;
      if (dx < xmin) {xmin = dx;}
      if (dx > xmax) {xmax = dx;}
    });
    const [intercept, slope] = ols(X, Y, XY, X2);
    const fn = (x: number) => (slope * Math.log(x)) / lb + intercept;
    return interpose(xmin, xmax, fn);
  }

  const result = useLoess ? loessFit(xyValues, bandwidth) : logFit(xyValues);
  return result.map(([x, y]) => ({ x, y }));
}

// Shared, lazily-created pool. Scatter fits are interactive, so a persistent pool avoids per-interaction
// worker spawn; groups fan out across cores.
let fitPool: Pool<ScatterFitPayload, Array<{ x: number; y: number }>> | null = null;
function getFitPool(): Pool<ScatterFitPayload, Array<{ x: number; y: number }>> {
  if (!fitPool) {fitPool = createPool<ScatterFitPayload, Array<{ x: number; y: number }>>(scatterRegressionTask);}
  return fitPool;
}

/** Compute per-group fit curves off the main thread (inline fallback when workers absent). Order preserved. */
export function computeScatterFitValues(
  inputs: ScatterFitPayload[]
): Promise<Array<Array<{ x: number; y: number }>>> {
  if (inputs.length === 0) {return Promise.resolve([]);}
  return getFitPool().map(inputs);
}
